import { useState } from 'react';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import { useJournalStore } from '../hooks/useJournalStore';
import { getWidgetComponent, WIDGET_CATALOG } from '../widgets/WidgetRegistry';
import { HeaderNav } from '../components/HeaderNav';
import ThemeToggle from '../components/ThemeToggle';
import type { WidgetConfig, WidgetType } from '../types';

export function DashboardShell({ journalRootId }: { journalRootId?: string }) {
  const { layout, mode, setMode, updateLayout, addWidget, removeWidget } = useJournalStore();
  const [showCatalog, setShowCatalog] = useState(false);

  const gridItems = layout.widgets.map((w) => ({
    i: w.id,
    x: w.x,
    y: w.y,
    w: w.w,
    h: w.h,
    minW: w.minW,
    minH: w.minH,
  }));

  function handleLayoutChange(newLayout: GridLayout.Layout[]) {
    const updated = layout.widgets.map((w) => {
      const item = newLayout.find((l) => l.i === w.id);
      if (!item) return w;
      return { ...w, x: item.x, y: item.y, w: item.w, h: item.h };
    });
    updateLayout(updated);
  }

  function handleAddWidget(type: WidgetType) {
    const catalog = WIDGET_CATALOG.find((c) => c.type === type);
    if (!catalog) return;
    const id = `w-${type}-${Date.now().toString(36)}`;
    const widget: WidgetConfig = {
      id,
      type,
      title: catalog.label,
      x: 0,
      y: Infinity,
      w: catalog.defaultW,
      h: catalog.defaultH,
      minW: 2,
      minH: 2,
    };
    addWidget(widget);
    setShowCatalog(false);
  }

  return (
    <div className="h-screen bg-tower-bg text-tower-text flex flex-col overflow-hidden">
      {/* Top bar */}
      <header className="h-10 bg-tower-surface border-b border-tower-border flex items-center px-4 gap-4 shrink-0">
        <span className="text-sm font-mono font-bold text-tower-accent tracking-wider">MAESTRO</span>
        <div className="h-4 w-px bg-tower-border" />
        <button
          onClick={() => setMode('flight')}
          className={`text-xs px-2 py-0.5 rounded ${mode === 'flight' ? 'bg-tower-accent text-textOnPrimary' : 'text-tower-muted hover:text-tower-text'}`}
        >
          Flightmode
        </button>
        <button
          onClick={() => setMode('tower')}
          className={`text-xs px-2 py-0.5 rounded ${mode === 'tower' ? 'bg-tower-accent text-textOnPrimary' : 'text-tower-muted hover:text-tower-text'}`}
        >
          Tower
        </button>
        <button
          onClick={() => setMode('studio')}
          className={`text-xs px-2 py-0.5 rounded ${mode === 'studio' ? 'bg-success text-textOnPrimary' : 'text-tower-muted hover:text-tower-text'}`}
        >
          Studio
        </button>
        <div className="flex-1" />
        <button
          onClick={() => setShowCatalog(!showCatalog)}
          className="text-xs text-tower-muted hover:text-tower-accent"
        >
          + Add Widget
        </button>
        <div className="h-4 w-px bg-tower-border" />
        <HeaderNav />
        <ThemeToggle className="p-1" />
      </header>

      {/* Widget catalog dropdown */}
      {showCatalog && (
        <div className="absolute top-10 right-4 z-50 bg-tower-surface border border-tower-border rounded-lg shadow-xl p-3 w-64">
          <div className="text-xs text-tower-muted mb-2 font-semibold">Widget Catalog</div>
          {WIDGET_CATALOG.map((cat) => (
            <button
              key={cat.type}
              onClick={() => handleAddWidget(cat.type)}
              className="w-full text-left px-2 py-1.5 text-xs hover:bg-tower-border/50 rounded flex flex-col"
            >
              <span className="text-tower-text">{cat.label}</span>
              <span className="text-tower-muted">{cat.description}</span>
            </button>
          ))}
        </div>
      )}

      {/* Grid */}
      <main className="flex-1 overflow-auto p-2">
        <GridLayout
          layout={gridItems}
          cols={12}
          rowHeight={60}
          width={window.innerWidth - 16}
          onLayoutChange={handleLayoutChange}
          draggableHandle=".widget-drag-handle"
          compactType="vertical"
          margin={[8, 8]}
        >
          {layout.widgets.map((w) => {
            const Component = getWidgetComponent(w.type);
            return (
              <div key={w.id} className="bg-tower-surface border border-tower-border rounded-lg overflow-hidden flex flex-col">
                <div className="widget-drag-handle flex items-center justify-between px-2 py-1 bg-tower-border/30 cursor-move shrink-0">
                  <span className="text-xs text-tower-muted font-semibold">{w.title}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${w.title} widget`}
                    title="Remove widget"
                    // Stop react-grid-layout's drag handler (mousedown/touch on the
                    // .widget-drag-handle) from swallowing the click so removeWidget fires.
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); removeWidget(w.id); }}
                    className="text-tower-muted hover:text-tower-danger text-sm leading-none px-1 cursor-pointer"
                  >
                    ✕
                  </button>
                </div>
                <div className="flex-1 overflow-hidden">
                  {Component ? <Component journalRootId={journalRootId} widgetProps={w.props} /> : (
                    <div className="p-3 text-tower-muted text-xs">Unknown widget: {w.type}</div>
                  )}
                </div>
              </div>
            );
          })}
        </GridLayout>
      </main>
    </div>
  );
}
